import type {
  LngLat,
  Shape,
  ShapeOpening,
} from "../adapters/types";
import { circleRing } from "./circleRing";

/**
 * Comment une forme arrête un itinéraire : `free`, on la traverse ; `blocked`, on la contourne ;
 * `walls`, ses murs arrêtent mais on circule dedans et on y entre par ses ouvertures.
 */
export type ShapePassage = "free" | "blocked" | "walls";

export function shapePassage(shape: Shape): ShapePassage {
  if (shape.walkable) return "free";

  return shape.walled ? "walls" : "blocked";
}

/** Sommets distincts de l'anneau `ring` d'une forme : 0 pour le contour, 1 et suivants pour les trous. */
export function ringVertices(shape: Shape, ring: number): LngLat[] {
  if (shape.shapeKind === "circle") return ring === 0 ? withoutClosingPoint(circleRing(shape.coordinates as [LngLat, LngLat])) : [];
  const points = ring === 0 ? shape.coordinates : shape.holes?.[ring - 1];
  if (!points) return [];

  return withoutClosingPoint(points);
}

export function withoutClosingPoint(points: LngLat[]): LngLat[] {
  const first = points[0];
  const last = points[points.length - 1];

  return points.length > 1 && first && last && first[0] === last[0] && first[1] === last[1] ? points.slice(0, -1) : points;
}

/** Le point de l'anneau fermé `vertices` à la position `position` (numéro de segment + fraction). */
export function pointAtPosition(vertices: LngLat[], position: number): LngLat {
  const count = vertices.length;
  const wrapped = ((position % count) + count) % count;
  const segment = Math.floor(wrapped);
  const t = wrapped - segment;
  const from = vertices[segment]!;
  const to = vertices[(segment + 1) % count]!;

  return [from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t];
}

/** Position, sur l'anneau fermé `vertices`, du point de l'anneau le plus proche de `point`. */
export function positionOfPoint(vertices: LngLat[], point: LngLat): number {
  let best = 0;
  let bestDistance = Infinity;

  for (let i = 0; i < vertices.length; i++) {
    const from = vertices[i]!;
    const to = vertices[(i + 1) % vertices.length]!;
    const dx = to[0] - from[0];
    const dy = to[1] - from[1];
    const lengthSquared = dx * dx + dy * dy;
    const t = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1, ((point[0] - from[0]) * dx + (point[1] - from[1]) * dy) / lengthSquared));
    const distance = Math.hypot(from[0] + t * dx - point[0], from[1] + t * dy - point[1]);

    if (distance < bestDistance) {
      bestDistance = distance;
      best = t === 1 ? (i + 1) % vertices.length : i + t;
    }
  }

  return best;
}

/**
 * Recale les ouvertures de l'anneau `ring` quand ses sommets passent de `before` à `after` (ajout ou
 * retrait d'un sommet) : chaque extrémité garde sa place sur le mur, seule sa numérotation change.
 */
export function remapOpenings(
  openings: ShapeOpening[] | undefined,
  ring: number,
  before: LngLat[],
  after: LngLat[],
): ShapeOpening[] | undefined {
  if (!openings?.length) return openings;

  return openings.map((opening) => opening.ring !== ring ? opening : {
    ring,
    from: positionOfPoint(after, pointAtPosition(before, opening.from)),
    to: positionOfPoint(after, pointAtPosition(before, opening.to)),
  });
}

/** Le tracé d'une ouverture le long de son anneau, sommets intermédiaires compris. */
export function openingPath(vertices: LngLat[], opening: ShapeOpening): LngLat[] {
  const count = vertices.length;
  const end = opening.from + ((((opening.to - opening.from) % count) + count) % count);
  const path = [pointAtPosition(vertices, opening.from)];
  for (let vertex = Math.floor(opening.from) + 1; vertex < end; vertex++) path.push(vertices[vertex % count]!);
  path.push(pointAtPosition(vertices, end));

  return path;
}

/**
 * Les murs d'une forme : chaque anneau, contour et cours, privé de ses ouvertures, en tronçons
 * ouverts. Un anneau sans ouverture reste un tronçon fermé (son premier point répété en dernier).
 * Des ouvertures qui se chevauchent n'en font qu'une.
 */
export function wallChains(shape: Shape): LngLat[][] {
  const ringCount = 1 + (shape.holes?.length ?? 0);
  const chains: LngLat[][] = [];

  for (let ring = 0; ring < ringCount; ring++) {
    const vertices = ringVertices(shape, ring);
    if (vertices.length < 2) continue;

    const gaps = mergedGaps((shape.openings ?? []).filter((opening) => opening.ring === ring), vertices.length);
    if (!gaps.length) {
      chains.push([...vertices, vertices[0]!]);
      continue;
    }

    for (let i = 0; i < gaps.length; i++) {
      const start = gaps[i]!.end;
      const next = gaps[(i + 1) % gaps.length]!;
      const end = next.start + (i + 1 === gaps.length ? vertices.length : 0);
      if (end - start <= 0) continue;

      const chain = [pointAtPosition(vertices, start)];
      for (let vertex = Math.floor(start) + 1; vertex < end; vertex++) chain.push(vertices[vertex % vertices.length]!);
      chain.push(pointAtPosition(vertices, end));
      chains.push(chain);
    }
  }

  return chains;
}

/**
 * Les ouvertures d'un anneau de `count` segments, en intervalles `[start, end]` triés et fusionnés,
 * `start` dans `[0, count)` et `end` éventuellement au-delà de `count` quand l'ouverture enjambe le
 * sommet 0.
 */
function mergedGaps(openings: ShapeOpening[], count: number): { start: number; end: number }[] {
  const intervals = openings
    .map(({ from, to }) => {
      const start = ((from % count) + count) % count;
      const length = (((to - from) % count) + count) % count;
      return { start, end: start + length };
    })
    .filter(({ start, end }) => end > start)
    .sort((a, b) => a.start - b.start);

  const merged: { start: number; end: number }[] = [];
  for (const interval of intervals) {
    const last = merged[merged.length - 1];
    if (last && interval.start <= last.end) last.end = Math.max(last.end, interval.end);
    else merged.push({ ...interval });
  }

  // Une ouverture qui enjambe le sommet 0 peut recouvrir le début de la première.
  const first = merged[0];
  const last = merged[merged.length - 1];
  if (first && last && merged.length > 1 && last.end - count >= first.start) {
    last.end = Math.max(last.end, first.end + count);
    merged.shift();
  }

  return merged;
}

/** Lit des ouvertures venues du JSON : seules celles faites de trois nombres finis sont gardées. */
export function parseOpenings(raw: unknown): ShapeOpening[] | undefined {
  if (!Array.isArray(raw)) return undefined;

  const openings = raw.filter((opening: unknown): opening is ShapeOpening => {
    if (!opening || typeof opening !== "object") return false;
    const { ring, from, to } = opening as Record<string, unknown>;

    return [ring, from, to].every((value) => typeof value === "number" && Number.isFinite(value));
  }).map(({ ring, from, to }) => ({ ring, from, to }));

  return openings.length ? openings : undefined;
}
